/**
 * Patrón de Ruptura Quantum — renderer.
 *
 * Solo pinta: la detección vive en el motor legacy (vía adapter.js) y las
 * estadísticas en stats.js. Todo el dibujo pasa por createChartDrawingApi
 * (addSeries / addCustomSeries / addPriceLine / addLabel / addMarker) — nunca
 * LWC directo ni DOM propio sobre el chart (regla C.2), y ninguna serie toca
 * la escala de precios (autoscaleInfoProvider: () => null).
 */

import { LineStyle } from 'lightweight-charts'
import { log as debugLog } from '@/utils/logger'
import { ZoneFillSeries } from './ZoneFillSeries.js'
import {
  QUANTUM_COLORS,
  ZONE_OPACITY,
  LABEL_FONT,
  resolveConfig,
  resolveColor,
  resolveColors,
  withAlpha,
} from './constants.js'

// Límites de historial pintado (patrón del motor legacy, que recorta a 40).
const MAX_PATTERNS_DRAWN = 10
const MAX_SIGNALS_DRAWN = 40

const COMMON_LINE_OPTS = {
  lastValueVisible: false,
  priceLineVisible: false,
  crosshairMarkerVisible: false,
  autoscaleInfoProvider: () => null,
}

/** Decimales razonables según magnitud del precio (mismo criterio que bollinger). */
function formatPrice(v) {
  if (!Number.isFinite(v)) return '—'
  if (Math.abs(v) >= 1000) return v.toFixed(1)
  if (Math.abs(v) >= 1) return v.toFixed(2)
  return v.toFixed(4)
}

function formatPct(entry, price) {
  if (!Number.isFinite(entry) || entry === 0 || !Number.isFinite(price)) return '—'
  const pct = ((price - entry) / entry) * 100
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`
}

// ── Guardas de pintado (defensa en profundidad, hotfix P0 2026-08-03) ───────
// lightweight-charts NO valida síncronamente: un null/NaN pasa silencioso por
// setData/createPriceLine y revienta DIFERIDO en el siguiente frame de paint
// ("Uncaught Error: Value is null"), con el renderContext apuntando a la
// última operación instrumentada — no necesariamente a la culpable. El core
// (createChartDrawingApi) envuelve addSeries.setData y valida addPriceLine,
// pero NO envuelve addCustomSeries.setData: esa es la única vía sin filtro,
// así que TODA la validación de custom series es responsabilidad nuestra.
// Regla: ningún dato inválido (time null/''/NaN, valor no finito) cruza una
// llamada a api.* desde este renderer; nivel ausente = no se pinta.
function isValidTime(t) {
  return t != null && t !== '' && !Number.isNaN(t)
}

function isValidPrice(v) {
  return Number.isFinite(v)
}

/** Punto {time, value} válido para LineSeries. */
function isValidLinePoint(p) {
  return p != null && isValidTime(p.time) && isValidPrice(p.value)
}

/** Punto {time, upper, lower} válido para ZoneFillSeries. */
function isValidZonePoint(p) {
  return p != null && isValidTime(p.time) && isValidPrice(p.upper) && isValidPrice(p.lower)
}

/** 2 puntos inicio→fin para una LineSeries, validados (sin NaN ni times null). */
function linePoints(from, to) {
  if (from?.time == null || to?.time == null) return null
  if (!Number.isFinite(from?.price) || !Number.isFinite(to?.price)) return null
  if (from.time === to.time) return [{ time: from.time, value: from.price }]
  return [
    { time: from.time, value: from.price },
    { time: to.time, value: to.price },
  ]
}

function addLine(api, title, color, lineWidth, lineStyle, points) {
  // Re-valida en el punto de llamada: una serie con un solo punto inválido
  // no se crea (LWC rechazaría toda la serie en el wrapper del core).
  const clean = Array.isArray(points) ? points.filter(isValidLinePoint) : []
  if (clean.length === 0) return
  const s = api.addSeries(c => c.addLineSeries({
    title,
    color,
    lineWidth,
    lineStyle,
    ...COMMON_LINE_OPTS,
  }))
  if (s) s.setData(clean)
}

/** Custom series de relleno con TODOS sus puntos validados antes de setData. */
function addZoneSeries(api, points, fillColor, chart) {
  const clean = (Array.isArray(points) ? points.filter(isValidZonePoint) : [])
    // LWC rechaza (throw) series con tiempos desordenados o duplicados; un
    // setData fallido dejaría la serie VACÍA y visible → "Value is null" en el
    // siguiente paint/crosshair. Orden + dedup + retirada en caso de fallo.
    .sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0))
    .filter((p, i, arr) => i === 0 || p.time !== arr[i - 1].time)
  if (clean.length < 2) return
  const s = api.addCustomSeries(new ZoneFillSeries(), {
    color: 'rgba(0,0,0,0)',
    fillColor,
    lineWidth: 0,
    ...COMMON_LINE_OPTS,
  })
  if (!s) return
  try {
    s.setData(clean.map(p => ({ time: p.time, upper: p.upper, lower: p.lower, color: p.color ?? fillColor })))
  } catch {
    // Nunca dejar una serie custom vacía visible en el chart.
    try { chart?.removeSeries?.(s) } catch { /* noop */ }
  }
}

export function renderPrq(chart, candleSeries, result, config, api) {
  if (!result || !api) return
  const cfg = resolveConfig(config)
  if (cfg.enabled === false || result.gated) return

  const patterns = Array.isArray(result.patterns) ? result.patterns : []
  const signals = Array.isArray(result.signals) ? result.signals : []
  if (patterns.length === 0 && signals.length === 0) return

  const colors = resolveColors(cfg)
  const zoneColor = resolveColor(cfg.zoneColor, null)

  // Velas visibles: para el extremo derecho de niveles y el barrido de zonas.
  const bars = candleSeries && typeof candleSeries.data === 'function' ? candleSeries.data() : []
  const lastBar = Array.isArray(bars) && bars.length > 0 ? bars[bars.length - 1] : null
  const lastTime = lastBar?.time ?? null

  // ── Relleno del canal (por sesgo de ruptura y fuerza de la señal) ────────
  // Se crea ANTES que las líneas para que quede debajo en el orden de
  // pintado. Ojo: las series de lightweight-charts (custom incluidas) siempre
  // se dibujan ENCIMA de la serie de velas — no hay forma de mandar el
  // relleno detrás de las velas sin inventar panes (descartado por spec), así
  // que la opacidad baja es la que mantiene las velas legibles.
  if (cfg.showChannelFill) {
    for (const pat of patterns.slice(-MAX_PATTERNS_DRAWN)) {
      if (!Array.isArray(pat.fill) || pat.fill.length < 2) continue
      const base = pat.bias === 'bull'
        ? QUANTUM_COLORS.bull
        : pat.bias === 'bear'
          ? QUANTUM_COLORS.bear
          : QUANTUM_COLORS.neutral
      // Opacidad modulada por fuerza (strength de la señal asociada; null si
      // el canal no rompió → se trata como débil).
      const s = Number.isFinite(pat.strength) ? pat.strength : null
      const alpha = s == null || s < 40 ? 0.05 : s <= 70 ? 0.08 : 0.12
      const fillColor = withAlpha(base, alpha)
      addZoneSeries(api, pat.fill.map(p => ({ ...p, color: fillColor })), fillColor, chart)
    }
  }

  // ── Líneas del patrón (resistencia/soporte congeladas por el adapter) ────
  if (cfg.showPatternLines) {
    for (const pat of patterns.slice(-MAX_PATTERNS_DRAWN)) {
      const res = linePoints(pat.resistance?.[0], pat.resistance?.[1])
      const sup = linePoints(pat.support?.[0], pat.support?.[1])
      if (res) addLine(api, 'PRQ Resistencia', colors.resistance, cfg.lineWidth, LineStyle.Solid, res)
      if (sup) addLine(api, 'PRQ Soporte', colors.support, cfg.lineWidth, LineStyle.Solid, sup)
    }
  }

  // ── Etiquetas de señal (convención de la caja de posición de STQ) ────────
  const signalsToDraw = signals.slice(-MAX_SIGNALS_DRAWN)
  if (cfg.showSignalLabels) {
    for (const sig of signalsToDraw) {
      if (!isValidTime(sig.time) || !Number.isFinite(sig.entry)) continue
      const isBull = sig.direction === 'LONG'
      const color = isBull ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear
      api.addLabel({
        time: sig.time,
        price: sig.entry,
        text: `${sig.direction} ${formatPrice(sig.entry)}`,
        color,
        bgColor: withAlpha(color, 0.12),
        boxStyle: 'flag',
        isBull,
        font: LABEL_FONT,
      })
    }
  }

  // ── Niveles + zonas + etiquetas de eje: señal activa o última (patrón
  // legacy: levelsSource = activeTrade || último signal). Se mantienen
  // congelados tras el outcome final; no se borran. ─────────────────────────
  const levelsSignal = result.activeSignal || result.lastSignal || null
  if (levelsSignal && Number.isFinite(levelsSignal.entry)) {
    const { entry, sl, tp1, tp2, tp3, time: sigTime } = levelsSignal

    if (cfg.showZones && isValidTime(sigTime) && Array.isArray(bars) && bars.length > 0) {
      // Zona de riesgo (entry↔SL, bear) y de beneficio (entry↔TP3, bull),
      // alpha fijo ZONE_OPACITY. Se barren las velas desde la señal.
      // Si tp3 no resolviera (no debería: el motor siempre lo da), la zona de
      // beneficio cae a entry↔TP2.
      const rewardTarget = Number.isFinite(tp3) ? tp3 : tp2
      const span = bars.filter(b => isValidTime(b.time) && b.time >= sigTime)
      if (span.length >= 2 && Number.isFinite(sl) && Number.isFinite(rewardTarget)) {
        const riskColor = withAlpha(zoneColor ?? QUANTUM_COLORS.bear, ZONE_OPACITY)
        const rewardColor = withAlpha(zoneColor ?? QUANTUM_COLORS.bull, ZONE_OPACITY)
        const zones = [
          { upper: Math.max(entry, sl), lower: Math.min(entry, sl), color: riskColor },
          { upper: Math.max(entry, rewardTarget), lower: Math.min(entry, rewardTarget), color: rewardColor },
        ]
        for (const z of zones) {
          if (!(z.upper > z.lower)) continue
          addZoneSeries(api, span.map(b => ({ time: b.time, upper: z.upper, lower: z.lower, color: z.color })), z.color, chart)
        }
      }
    }

    if (cfg.showLevels && isValidTime(sigTime)) {
      // Desde la vela de señal hacia la derecha, hasta la última vela (el
      // motor legacy extiende labelOffset barras más allá; aquí basta el
      // borde de datos para no inventar aritmética de fechas string).
      const endTime = lastTime != null && lastTime > sigTime ? lastTime : sigTime
      const levelLine = (price, color, width, style) => {
        if (!Number.isFinite(price)) return
        addLine(api, 'PRQ Nivel', color, width, style, [
          { time: sigTime, value: price },
          ...(endTime !== sigTime ? [{ time: endTime, value: price }] : []),
        ])
      }
      levelLine(entry, colors.entry, 1, LineStyle.Solid)
      levelLine(sl, colors.sl, 1.5, LineStyle.Solid)
      levelLine(tp1, colors.tp, 1.5, LineStyle.Solid)
      levelLine(tp2, colors.tp, 1.5, cfg.tp2LineStyle === 'dashed' ? LineStyle.Dashed : LineStyle.Solid)
      // TP3 siempre con trazo más largo para mantener la jerarquía visual
      // TP1 sólida < TP2 punteada < TP3 punteada larga. Si el usuario pone
      // TP2 sólida, TP3 se degrada a Dashed (sigue distinto de TP1/TP2).
      levelLine(tp3, colors.tp, 1.5, cfg.tp2LineStyle === 'dashed' ? LineStyle.LargeDashed : LineStyle.Dashed)

      // Etiquetas de nivel en el eje de precio (patrón ICT: price line
      // invisible con axisLabelVisible, sin línea que cruce todo el chart).
      const addPriceLabel = (price, title, color) => {
        if (!Number.isFinite(price)) return
        // Instrumentación P0 (G.5: gateada por logger, silenciosa en prod):
        // toda priceLine del indicador queda registrada con su valor — si el
        // crash "Value is null" vuelve, el log dice qué nivel y con qué valor.
        debugLog('[prq] addPriceLine', { title, price })
        api.addPriceLine({ price, title, color, lineVisible: false, axisLabelVisible: true })
      }
      addPriceLabel(entry, `Entrada ${formatPrice(entry)}`, colors.entry)
      addPriceLabel(sl, `SL ${formatPrice(sl)} (${formatPct(entry, sl)})`, colors.sl)
      addPriceLabel(tp1, `TP1 ${formatPrice(tp1)} (${formatPct(entry, tp1)})`, colors.tp)
      addPriceLabel(tp2, `TP2 ${formatPrice(tp2)} (${formatPct(entry, tp2)})`, colors.tp)
      addPriceLabel(tp3, `TP3 ${formatPrice(tp3)} (${formatPct(entry, tp3)})`, colors.tp)
    }

    // Marcadores de outcome en closeTime: ✓ en TP alcanzado (bull; result
    // 'win' del motor = TP3 alcanzado o BE exit tras TP1), ✗ en SL (bear).
    // Timeout no lleva marcador: el rediseño no lo dibuja (el outcome crudo
    // sigue visible en el badge).
    if (cfg.showOutcomeMarkers) {
      for (const sig of signalsToDraw) {
        if (!isValidTime(sig.closeTime)) continue
        const isLong = sig.direction === 'LONG'
        if (sig.outcome === 'win') {
          api.addMarker({
            time: sig.closeTime,
            position: isLong ? 'aboveBar' : 'belowBar',
            color: QUANTUM_COLORS.bull,
            shape: 'circle',
            text: '✓',
            size: 1,
          })
        } else if (sig.outcome === 'loss') {
          api.addMarker({
            time: sig.closeTime,
            position: isLong ? 'belowBar' : 'aboveBar',
            color: QUANTUM_COLORS.bear,
            shape: 'circle',
            text: '✗',
            size: 1,
          })
        }
      }
    }
  }
}
